// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { xScreen } from "@bg/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AccountPaymentProfileModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import * as React from "react";
import { css, ThemeProps, withTheme } from "styled-components";

export interface SavedPaymentsCardProps {
    savedPayment: AccountPaymentProfileModel;
    useAsDefaultClickHandler: (savedPayment: AccountPaymentProfileModel) => void;
    editClickHandler: (savedPayment: AccountPaymentProfileModel) => void;
    deleteClickHandler: (savedPayment: AccountPaymentProfileModel) => void;
}
type Props = SavedPaymentsCardProps & ThemeProps<BaseTheme>;

export interface SavedPaymentsCardStyles {
    cardHeaderWrapper?: InjectableCss;
    nickNameAndImageContainer?: InjectableCss;
    cardTypeImage?: LazyImageProps;
    descriptionText?: TypographyPresentationProps;
    cardContainer?: InjectableCss;
    nameOnCardAndDefaultTriangleRow?: InjectableCss;
    nameOnCardAndExpiration?: InjectableCss;
    defaultCardTriangle?: InjectableCss;
    defaultCardTriangleContentStyle?: InjectableCss;
    nameOnCardText?: TypographyPresentationProps;
    expirationText?: TypographyPresentationProps;
    actionsRow?: InjectableCss;
    actionsContainer?: InjectableCss;
    actionsButtonsContainer?: InjectableCss;
    useAsDefaultGridItem?: GridItemProps;
    useAsDefaultCheckbox?: CheckboxPresentationProps;
    editButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    billingAddressText?: TypographyPresentationProps;
}

export const cardListStyles: SavedPaymentsCardStyles = {
    cardContainer: {
        css: css`
            background: ${getColor("white")};
            border: 1px solid ${getColor("common.ui3")};
            color: ${({ theme }) => {
                return resolveColor("text.main", theme);
            }};
            width: 100%;
            min-height: 150px;
            position: relative;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            &:hover {
                .actions-container {
                    opacity: 1;
                }
            }
            padding: 0;
        `,
    },
    cardHeaderWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 20px;
        `,
    },
    nickNameAndImageContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            svg {
                width: 50px;
            }
        `,
    },
    cardTypeImage: {
        imgProps: {
            style: {
                width: "auto",
            },
        },
    },
    descriptionText: {
        css: css`
            font-size: ${fontConfig.fontSize.large}px;
            font-weight: ${fontConfig.weight.bold};
        `,
    },
    nameOnCardAndDefaultTriangleRow: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            padding: 0 20px;
        `,
    },
    nameOnCardAndExpiration: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
        `,
    },
    defaultCardTriangle: {
        css: css`
            position: absolute;
            top: 100%;
            left: 0;
            transform: translateY(-100%);
            border-left: 50px solid ${getColor("success.main")};
            border-top: 50px solid ${getColor("primary.contrast")};
            display: inline-block;
        `,
    },
    defaultCardTriangleContentStyle: {
        css: css`
            position: absolute;
            top: 100%;
            left: -55px;
            transform: translateY(calc(-100% - 10px)) rotate(45deg);
            color: ${getColor("primary.contrast")};
            font-size: ${fontConfig.fontSize.legend}px;
            line-height: ${fontConfig.lineHeight.legend};
            border: none;
            width: 45px;
            z-index: 1;
        `,
    },
    nameOnCardText: {
        css: css`
            font-size: ${fontConfig.fontSize.small}px;
        `,
    },
    expirationText: {
        css: css`
            font-size: ${fontConfig.fontSize.xxSmall}px;
            margin-bottom: 10px;
        `,
    },
    actionsRow: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 20px;
        `,
    },
    actionsContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
                opacity: 1;
                position: static;
                margin: auto;
            }
            ${xScreen("lte", "md")} {
                flex-direction: row;
                opacity: 1;
                position: static;
            }
            height: 75px;
            background: ${getColor("common.ui2")};
            color: ${getColor("text.main")};
            display: flex;
            padding: 10px 20px;
            left: 0;
            position: absolute;
            bottom: 0;
            width: 100%;
            opacity: 0;
            transition: opacity 0.3s ease;
            z-index: 2;
        `,
    },
    useAsDefaultGridItem: {
        width: 6,
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },
    actionsButtonsContainer: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    editButton: {
        sizeVariant: "small",
        css: css`
            margin-right: 10px;
            word-break: keep-all;

            ${xScreen("lte", "md")} {
                padding: 0 15px;
            }
        `,
    },
    deleteButton: {
        sizeVariant: "small",
        variant: "secondary",
        css: css`
            word-break: keep-all;
            ${xScreen("lte", "md")} {
                padding: 0 15px;
            }
        `,
    },
    billingAddressText: {
        css: css`
            width: 100%;
            align-items: flex-start;
        `,
    },
};

const styles = cardListStyles;

const SavedPaymentsCard: React.FC<Props> = ({
    savedPayment,
    theme,
    useAsDefaultClickHandler,
    editClickHandler,
    deleteClickHandler,
}) => {
    const getCardIcon = (cardType: string) => {
        if (cardType.toLowerCase() === "american express") {
            return <Icon src="Amex" />;
        }
        if (cardType.toLowerCase() === "mastercard") {
            return <Icon src="Mastercard" />;
        }
        if (cardType.toLowerCase() === "discover") {
            return <Icon src="Discover" />;
        }
        if (cardType.toLowerCase() === "visa") {
            return <Icon src="Visa" />;
        }
        return "";
    };

    return (
        <StyledWrapper {...styles.cardContainer} data-test-selector={`cardBody-${savedPayment.id}`}>
            <StyledWrapper>
                <StyledWrapper {...styles.cardHeaderWrapper} data-test-selector={`cardHeader-${savedPayment.id}`}>
                    <StyledWrapper {...styles.nickNameAndImageContainer}>
                        <Typography {...styles.descriptionText}>{savedPayment.description}</Typography>
                        {getCardIcon(savedPayment.cardType)}
                    </StyledWrapper>
                    <Typography>
                        {savedPayment.maskedCardNumber.substring(savedPayment.maskedCardNumber.length - 4)}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.nameOnCardAndDefaultTriangleRow}>
                <StyledWrapper
                    css={css`
                        width: 100%;
                    `}
                >
                    <Typography {...styles.billingAddressText} aria-labelledby="billingAddress">
                        {[savedPayment.address1, savedPayment.address2, savedPayment.address3, savedPayment.address4]
                            .filter(o => !!o)
                            .join(", ")}
                        <br />
                        {`${savedPayment.city}, `}
                        {`${savedPayment.state}, `}
                        {`${savedPayment.postalCode}, `}
                        {savedPayment.country}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.nameOnCardAndExpiration}>
                    <Typography {...styles.nameOnCardText} aria-label={translate("Name on Card")}>
                        {savedPayment.cardHolderName}
                    </Typography>
                    <Typography {...styles.expirationText} aria-label={translate("expiration")}>
                        {savedPayment.expirationDate}
                    </Typography>
                </StyledWrapper>

                {savedPayment.isDefault && (
                    <StyledWrapper {...styles.defaultCardTriangle}>
                        <StyledWrapper {...styles.defaultCardTriangleContentStyle}>
                            {translate("Default")}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </StyledWrapper>

            <StyledWrapper {...styles.actionsContainer} className="actions-container">
                <Checkbox
                    onChange={() => useAsDefaultClickHandler(savedPayment)}
                    checked={savedPayment.isDefault}
                    data-test-selector="useAsDefaultButton"
                >
                    {translate("Use as Default")}
                </Checkbox>
                <StyledWrapper {...styles.actionsButtonsContainer}>
                    <Button
                        {...styles.editButton}
                        onClick={() => editClickHandler(savedPayment)}
                        data-test-selector="editButton"
                        aria-label="edit button"
                    >
                        {translate("Edit")}
                    </Button>
                    <Button
                        {...styles.deleteButton}
                        onClick={() => deleteClickHandler(savedPayment)}
                        data-test-selector="deleteButton"
                        aria-label="delete button"
                    >
                        {translate("Delete")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withTheme(SavedPaymentsCard);
